import type { QueryClient } from '@tanstack/react-query';

import type { OrganizationResponse } from '@/api/generated';

/** Where the account page keeps one organization's record. */
export function organizationKey(id: string) {
  return ['staff', 'organizations', id] as const;
}

/**
 * Write a saved account into the cache, keeping what only the detail read
 * carries.
 *
 * The page's header and cards read the cached record, so every save writes
 * its reply back at once rather than waiting for the refetch. But the replies
 * to an update and a status change carry `notification_allowance` as null -
 * the backend fills it on the detail read only - and writing one as it is
 * would blank this month's usage for the length of the round trip.
 *
 * The cached usage is kept while it still describes the saved limit. After an
 * allowance save that changes the limit, its remaining is out of date, so it
 * is dropped and the refetch the save waits for brings the new numbers. The
 * portal does not work out remaining itself; that rule is the backend's.
 */
export function storeSavedOrganization(
  queryClient: QueryClient,
  saved: OrganizationResponse,
): void {
  queryClient.setQueryData<OrganizationResponse>(organizationKey(saved.id), (cached) => {
    if (saved.notification_allowance) return saved;
    const kept = cached?.notification_allowance;
    const stillFits =
      kept !== null &&
      kept !== undefined &&
      kept.limit === saved.effective_monthly_notification_limit;
    return { ...saved, notification_allowance: stillFits ? kept : null };
  });
}
